/* global BaseUrl, ProfileEnabled, MobileMenuEnabled, SingleMvpd */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import Headroom from 'react-headroom';
import SmartBanner from 'react-smartbanner';
import { animateScroll } from 'react-scroll';
import ReactTimeout from 'react-timeout';
import _ from 'lodash';
import classNames from 'classnames';
import Icon from 'components/Icon';
import SearchBox from 'components/SearchBox';
import { getPreviewPassTime, getProviderObjectFromList } from 'helpers/AuthHelper';
import { removeLegacyAccountCookie } from 'helpers/Account';
import NetworkLink from 'components/NetworkLink';
import NetworkBar from 'components/NetworkBar';
import SignUpModalContainer from 'components/SignUpModalContainer';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
  isMobileBreakpoint,
} from 'helpers/BreakpointHelper';
import {
  helperGetHomeUrl,
  getCurrentPath,
  helperGetAccountUrl,
  helperGetBaseUrlSlug,
  helperGetPersonalitiesUrl,
  redirectToExternalUrl,
  refreshPage,
  getExternalSignInUrl,
  setPathForReEntryRedirect,
  isHomeRoute,
} from 'helpers/RouteHelper';
import {
  isPreviewPass,
  playerPause,
  playerPlay,
  getPlayerState,
  PLAYER_REFERENCE_GLOBAL,
} from 'helpers/PlayerHelper';
import {
  canDisplayUpsell,
  forceHttps,
  getFontClassName,
  getUrlTarget,
  isMvpdSubscription,
  isNationEntitled,
  isStaticRender,
  PAGE_TYPE_WATCHLIST,
  urlIsExternal,
} from 'helpers/ContentHelper';
import {
  OVERLAY_HEADER_SEARCH,
  OVERLAY_HEADER_DROPDOWN,
  OVERLAY_VIDEO_MODAL,
} from 'helpers/FilterHelper';
import { toBoolean, noop } from 'helpers/GlobalHelper';
import { getOptimizelyModalEnabled, getOptimizelySearchBoxEnabled } from 'helpers/OptimizelyHelper';
import { PAGE_NETWORK_UPSELL } from 'helpers/PageHelper';
import UpgradeButton from 'components/Buythrough/UpgradeButton';
import {
  Events,
  getTealiumCurrentBrand,
  getTealiumRedesignEvents,
  handleAnalytics,
} from '@foxcorp/delta-web-analytics';
import { fetchMVPDProvidersAction, setPreauthorizedResourcesAction } from 'index/AppActions';
import { withAuth } from 'index/AuthProvider';
import { fetchCollectionsOnHeaderClick } from 'screens/Collection/Actions';
import Style from 'app/themes/styleguide/nation/Header.module.scss';
import {
  Categories,
  ProviderLogo,
  SearchIcon,
  BuythroughProfileIcon,
  HamburgerButton,
  MobileCloseButton,
} from './DefaultHeaderComponents';
import { smartBannerSettings, determineBrowser, getPreviewPassTimeDiff } from './HeaderHelpers';
import DefaultHeaderStyle from './DefaultHeaderComponents/DefaultHeader.module.scss';

const BREAKPOINTS = (() => {
  const breakpoints = {
    [SITE_PLATFORM_WEB_LARGE]: {
      darkNavOnHomepage: false,
      darkNavOnListing: true,
      darkNavOnVideo: true,
      darkNavOnNetworkUpsellPage: true,
      smartBannerBar: false,
      foxAwesomeLayout: false,
      useHoverMenu: false,
      hamburgerMenuEnabled: false,
    },
    [SITE_PLATFORM_WEB]: {
      darkNavOnHomepage: false,
      darkNavOnListing: true,
      darkNavOnVideo: true,
      darkNavOnNetworkUpsellPage: true,
      smartBannerBar: false,
      foxAwesomeLayout: false,
      useHoverMenu: false,
      hamburgerMenuEnabled: false,
    },
    [SITE_PLATFORM_TABLET]: {
      darkNavOnHomepage: true,
      darkNavOnListing: true,
      darkNavOnVideo: true,
      darkNavOnNetworkUpsellPage: true,
      smartBannerBar: false,
      foxAwesomeLayout: false,
      useHoverMenu: false,
      allCollectionsInNav: true,
      hamburgerMenuEnabled: MobileMenuEnabled,
    },
    [SITE_PLATFORM_MOBILE]: {
      darkNavOnHomepage: true,
      darkNavOnListing: true,
      darkNavOnVideo: true,
      darkNavOnNetworkUpsellPage: true,
      smartBannerBar: true,
      foxAwesomeLayout: false,
      useHoverMenu: false,
      allCollectionsInNav: true,
      hamburgerMenuEnabled: MobileMenuEnabled,
    },
  };
  return breakpoints;
})();

const NAME = 'Header';

class Header extends Component {
  constructor(props) {
    super(props);
    const previewPassTime = getPreviewPassTime();
    const timeDiff = getPreviewPassTimeDiff(previewPassTime);
    const renderPreviewPass = !!(timeDiff !== null && timeDiff > 0);
    this.state = {
      providerSet: false,
      renderPreviewPass,
      renderProfileDropdown: false,
      previewPassTime,
    };

    this.timeout = null;
  }

  componentDidMount() {
    const { providerSet } = this.state;
    const { deviceId, fetchMVPDProviders, mvpdData } = this.props;
    const mvpd = mvpdData?.mvpd;
    const shouldFetchMVPDProviders = mvpd && !providerSet;
    const locationPath = window?.location?.pathname;
    this.currentLocation = isHomeRoute(locationPath)
      ? 'home'
      : getCurrentPath()
          .substring(1, locationPath.length - 1)
          .toLowerCase();

    if (shouldFetchMVPDProviders) fetchMVPDProviders(deviceId);
    this.startPreviewPassTimeout();
    document.addEventListener('mousedown', this.handleClickOutside);
    window.addEventListener('focus', this.handleOnWindowFocus);
    const pageLabel = this.getNavPageLabel();
    handleAnalytics({
      event: Events.TOP_LEVEL_SECTION_LANDING,
      payload: { pageName: `${this.currentLocation}:landing`, pageType: pageLabel },
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { mvpdProviders, setProvider, setIsAuthenticated, isAuthenticated, mvpdData } = nextProps;

    const {
      isAuthenticated: currentIsAuthenticated,
      setPreauthorizedResources,
      clearTimeout,
    } = this.props;
    let newState = {};
    let setStateCallback = () => {};
    const mvpdId = mvpdData?.mvpd;
    const { providerSet } = this.state;
    const didFetchProviders = mvpdProviders && !providerSet;
    const didAuthenticate = !currentIsAuthenticated && isAuthenticated;
    if (didFetchProviders || didAuthenticate) {
      const previewPassTime = getPreviewPassTime();
      const timeDiff = getPreviewPassTimeDiff(previewPassTime);
      const validTimeDiff = timeDiff !== null && timeDiff > 0;
      const renderPreviewPass = validTimeDiff || isPreviewPass(mvpdId);
      const preauthorizedResources = _.get(mvpdData, 'preauthorizedResources');
      const mvpdFetched = getProviderObjectFromList(mvpdProviders, mvpdId);
      if (mvpdId && mvpdFetched) {
        newState = Object.assign(newState, {
          providerSet: true,
          previewPassTime,
          renderPreviewPass,
        });
        setStateCallback = () => {
          if (setProvider) setProvider(mvpdId);
          if (setIsAuthenticated && !isPreviewPass(mvpdId)) {
            setIsAuthenticated(true);
          }
          if (preauthorizedResources) setPreauthorizedResources(preauthorizedResources);
          if (renderPreviewPass) {
            clearTimeout(this.timeout);
            this.startPreviewPassTimeout();
          }
        };
      }
    }
    if (!_.isEmpty(newState)) this.setState(newState, setStateCallback);
  }

  shouldComponentUpdate(nextProps) {
    const newProps = this.getChangingProps(nextProps);
    const oldProps = this.getChangingProps(this.props);
    return !_.isEqual(newProps, oldProps);
  }

  componentWillUnmount() {
    const { clearTimeout } = this.props;
    clearTimeout(this.timeout);
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleOnWindowFocus = async () => {
    const { account } = this.props;
    const isEntitled = isNationEntitled();

    if (!isEntitled && account) {
      removeLegacyAccountCookie();
      window.location.reload();
    }
  };

  getChangingProps = props => {
    const {
      accessToken,
      account,
      appView,
      breakpoint,
      buythroughScenario,
      campaignOverrides,
      coordinates,
      deviceId,
      dynamicNavigation,
      favoritesManager,
      geoLocationBlocked,
      isAuthenticated,
      isFreemium,
      isHeader,
      isHeaderPinned,
      isHomePage,
      isListPage,
      isLivePage,
      isNavScheduleRendered,
      isPlayerLoaded,
      isSeriesDetailPage,
      isSmartBannerEnabled,
      isStandalone,
      isVideoPage,
      isWatchPage,
      location,
      maintenance,
      mvpdData,
      mvpdProviders,
      overlay,
      params,
      preauthorizedResources,
      preauthRefreshComplete,
      provider,
      showAppDownloadPrompt,
      subscriptionPaused,
      useSimplified,
      visibleCollections,
    } = props;
    return {
      accessToken,
      account,
      appView,
      breakpoint,
      buythroughScenario,
      campaignOverrides,
      coordinates,
      deviceId,
      dynamicNavigation,
      favoritesManager,
      geoLocationBlocked,
      isAuthenticated,
      isFreemium,
      isHeader,
      isHeaderPinned,
      isHomePage,
      isListPage,
      isLivePage,
      isNavScheduleRendered,
      isPlayerLoaded,
      isSeriesDetailPage,
      isSmartBannerEnabled,
      isStandalone,
      isVideoPage,
      isWatchPage,
      location,
      maintenance,
      mvpdData,
      mvpdProviders,
      overlay,
      params,
      preauthorizedResources,
      preauthRefreshComplete,
      provider,
      showAppDownloadPrompt,
      subscriptionPaused,
      useSimplified,
      visibleCollections,
    };
  };

  setWrapperRef = node => {
    this.wrapperRef = node;
  };

  handleClickOutside = event => {
    if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
      const { renderProfileDropdown } = this.state;
      if (renderProfileDropdown) this.setState({ renderProfileDropdown: false });
    }
  };

  disableAuthentication = () => {
    const { disableAuthentication = false } = window.ExternalConfig;

    return disableAuthentication;
  };

  onNavClick = ({ e, text }) => {
    const { onNavClick, setOverlay, upcomingOverlayOpen } = this.props;
    this.callCollectionImpressionEvent();
    if (onNavClick) onNavClick(e);
    animateScroll.scrollTo(0, { duration: 125, smooth: true });
    setOverlay(OVERLAY_HEADER_SEARCH, false);
    setOverlay(OVERLAY_HEADER_DROPDOWN, false);
    setOverlay(OVERLAY_VIDEO_MODAL, false);
    upcomingOverlayOpen(false);
  };

  onSearchClick = e => {
    const { onSearchClick } = this.props;

    handleAnalytics({
      event: Events.GLOBAL_NAV_CLICKED,
      payload: { page_item_title: 'Search' },
    });
    this.callCollectionImpressionEvent();
    if (onSearchClick) {
      onSearchClick(e);
    }
  };

  onSignInClick = ({ text }) => {
    const { location } = this.props;
    this.callCollectionImpressionEvent();
    const { pathname } = location;
    setPathForReEntryRedirect(pathname);
    const signInUrl = getExternalSignInUrl();
    if (signInUrl) redirectToExternalUrl(signInUrl);
  };

  onLogoutClick = async e => {
    e.preventDefault();
    const { mvpdLogout } = this.props;
    await mvpdLogout().then(data => {
      if (data.success) {
        redirectToExternalUrl(helperGetHomeUrl());
      }
    });
  };

  onSignUpClick = ({ text, isJoinNow }) => {
    const { isFreemium, isSeriesDetailPage, onSimplifiedSignUpClick, location } = this.props;
    const { pathname } = location;
    setPathForReEntryRedirect(pathname);

    handleAnalytics({
      event: Events.UPGRADE_NOW_BUTTON_CLICKED,
      payload: {
        page_item_title: text,
        page_action_location: 'global nav',
      },
    });
    this.callCollectionImpressionEvent();

    if (isSeriesDetailPage) {
      handleAnalytics({
        event: Events.PURCHASE_CTA_CLICKED,
        payload: {
          page_item_title: text,
          page_item_subtitle: '',
          page_action_location: 'global nav',
          page_button_text: text,
        },
      });
    }
    if (getOptimizelyModalEnabled()) {
      onSimplifiedSignUpClick();
    } else {
      let signUpUrl = _.get(window.ExternalConfig, 'externalSignUpUrl');
      if (isJoinNow) {
        signUpUrl = window.ExternalConfig?.blackFridaySignUpUrl;
      }
      if (signUpUrl) {
        if (isFreemium) {
          signUpUrl = `${signUpUrl}/?freemium=true`;
          setPathForReEntryRedirect();
        }
        redirectToExternalUrl(signUpUrl);
      }
    }
    return null;
  };

  onUnfix = () => {
    const { setHeaderPin } = this.props;
    setHeaderPin(true);
  };

  onUnpin = () => {
    const { overlay, setHeaderPin } = this.props;
    if (overlay === OVERLAY_HEADER_SEARCH) return;
    setHeaderPin(false);
  };

  getNavPageLabel = () => {
    const locationPath = window?.location?.pathname;
    const navPageLabel = window?.ExternalConfig?.navigation?.find(({ label }) => {
      if (locationPath?.includes('live-news')) return 'Fox News +';
      return locationPath?.includes(label?.toLowerCase());
    });
    return navPageLabel?.label;
  };

  getGlobalNavItems = isAnonymous => {
    const {
      accessToken,
      account: userAccount,
      deviceId,
      fetchCollectionsOnHeaderClick,
    } = this.props;
    let data = window?.ExternalConfig?.navigation ?? [];
    const isSearchPage = getCurrentPath().includes('/find');
    let provider = null;
    let account = null;

    if (this.isMobileActive()) {
      provider = this.renderMobileProvider();
      account = this.renderMobileProfile();
    }
    if (isAnonymous) data = data?.filter(item => item.anonymous);
    const items = data?.map((item, idx) => {
      const { breakpoint } = this.props;
      const { useHoverMenu, hamburgerMenuEnabled } = BREAKPOINTS[breakpoint];
      const { label, link = '', screenUrl = '', hidden = false } = item;
      if (hidden) return null;
      if (label === 'divider') {
        const separatorKey = `separator-${idx}`;
        const separator = <div key={separatorKey} className={Style.dropdownSeparator} />;
        return !hamburgerMenuEnabled ? separator : null;
      }
      const baseUrlSlug = helperGetBaseUrlSlug();
      const locationPath = window?.location?.pathname;
      const fullLink = `${baseUrlSlug}${link}`;
      const isPersonalityUrl = _.includes(locationPath, helperGetPersonalitiesUrl());
      let linkIsActive = fullLink === locationPath;
      if (link !== '/') linkIsActive = _.includes(locationPath, link);
      if (isPersonalityUrl && locationPath !== helperGetPersonalitiesUrl()) linkIsActive = false;
      let styles = [Style.categoryItem];
      if (useHoverMenu) styles.push(Style.dropdownShowsMovies);
      if (linkIsActive && !isSearchPage) styles.push(Style.categoryItemActive);

      if (this.isMobileActive()) {
        styles = Style.categoryItemMobilev2;
      }
      const pageType = label.includes('featured') ? 'home' : label?.toLowerCase();
      const navItemProps = {
        key: _.uniqueId(),
        to: fullLink,
        href: fullLink,
        className: classNames(styles),
        onClick: e => {
          if (locationPath.includes(fullLink)) return;
          if (screenUrl) {
            fetchCollectionsOnHeaderClick({
              accessToken,
              account: userAccount,
              deviceId,
              panelsToPrefetch: 3,
              path: screenUrl,
              screenName: label,
            });
          }
          handleAnalytics({
            event: Events.GLOBAL_NAV_CLICKED,
            payload: {
              page_item_title: label,
              page_type: pageType,
              page_action_location: this.currentLocation,
            },
          });
          handleAnalytics({
            event: Events.TOP_LEVEL_SECTION_LANDING,
            payload: { page_name: locationPath, page_type: this.getNavPageLabel() },
          });
          if (pageType === PAGE_TYPE_WATCHLIST) {
            handleAnalytics({
              event: Events.BUTTON_LINK_SELECTED,
              payload: {
                page_button_text: label,
                page_name: 'delta:web:watchlist',
                page_type: 'watchlist',
              },
            });
          }
          this.onNavClick({ e });
        },
      };
      return this.renderLink(navItemProps, label, idx);
    });
    return [...items, account, provider];
  };

  getPageLocation = () => {
    if (this.isAuthFlowActive()) return 'authentication flow';
    return 'other';
  };

  callCollectionImpressionEvent = () => {
    const { account, collectionImpressionCalled, visibleCollections } = this.props;
    const tealiumRedesignEvents = getTealiumRedesignEvents({ account });
    if (tealiumRedesignEvents) {
      tealiumRedesignEvents.collectionImpression({ visibleCollections });
    }
    collectionImpressionCalled();
  };

  toggleProfileDropdown = () => {
    const { renderProfileDropdown: prevRenderProfileDropdown } = this.state;
    this.callCollectionImpressionEvent();
    this.setState({ renderProfileDropdown: !prevRenderProfileDropdown });
  };

  startPreviewPassTimeout() {
    const { setTimeout } = this.props;
    const { previewPassTime } = this.state;

    const timeDiff = getPreviewPassTimeDiff(previewPassTime);
    const shouldSetTimeout = !!(timeDiff !== null && timeDiff > 0);

    if (!shouldSetTimeout) return;

    this.timeout = setTimeout(() => {
      this.setState({ renderPreviewPass: false });
    }, timeDiff);
  }

  isAuthFlowActive() {
    const { overlay } = this.props;
    return overlay && _.includes(overlay, 'OVERLAY_MVPD_AUTH');
  }

  isMenuOpen() {
    const { overlay } = this.props;
    return overlay === OVERLAY_HEADER_DROPDOWN;
  }

  isMobile() {
    const { breakpoint } = this.props;
    return isMobileBreakpoint(breakpoint);
  }

  isMobileActive = () => {
    const { breakpoint } = this.props;
    const { hamburgerMenuEnabled } = BREAKPOINTS[breakpoint];
    return hamburgerMenuEnabled && this.isMenuOpen();
  };

  isSearchActive = () => {
    const { overlay } = this.props;
    return overlay === OVERLAY_HEADER_SEARCH;
  };

  isTablet() {
    const { breakpoint } = this.props;
    return breakpoint === SITE_PLATFORM_TABLET;
  }

  isWeb = () => {
    const { breakpoint } = this.props;
    const isWebLargeBreakpoint = breakpoint === SITE_PLATFORM_WEB_LARGE;
    const isWebBreakpoint = breakpoint === SITE_PLATFORM_WEB;
    return isWebLargeBreakpoint || isWebBreakpoint;
  };

  renderContent() {
    const {
      account,
      breakpoint,
      campaignOverrides,
      favoritesManager,
      geoLocationBlocked: isBlocked,
      isFreemium,
      isLivePage,
      isWatchPage,
      maintenance: isMaintenance,
      provider,
      setTriggerAuthCheck,
      subscriptionPaused,
      toggleMvpdAuthOverlay,
    } = this.props;
    const { providerSet, renderPreviewPass, renderProfileDropdown } = this.state;
    const { foxAwesomeLayout } = BREAKPOINTS[breakpoint];
    const contentClasses = foxAwesomeLayout ? [Style.foxAwesomeContent] : [Style.content];
    contentClasses.push(Style.fullWidth);

    const buythroughProfileIconProps = {
      account,
      favoritesManager,
      isFreemium,
      isWeb: this.isWeb,
      renderProfileDropdown,
      setWrapperRef: this.setWrapperRef,
      toggleProfileDropdown: this.toggleProfileDropdown,
    };

    const profileIcon = BuythroughProfileIcon(buythroughProfileIconProps);

    const isSimplifiedHeader = isBlocked || isMaintenance;

    if (isSimplifiedHeader) {
      contentClasses.push(Style.maintenance);
      return <div className={classNames(contentClasses)}>{this.renderIndexLink()}</div>;
    }

    const menuOpen = this.isMenuOpen();
    const isSearchActive = this.isSearchActive();

    const rightContentClasses = {
      [DefaultHeaderStyle.hideContent]: menuOpen,
      [DefaultHeaderStyle.rightContent]: true,
    };
    const categoryProps = {
      campaignOverrides,
      desktopCategories: true,
      getGlobalNavItems: this.getGlobalNavItems,
      isAnonymous: false,
      isLivePage,
      isMobileActive: this.isMobileActive,
      isWatchPage,
      menuOpen,
    };
    const providerProps = {
      isSearchActive,
      onLogoutClick: this.onLogoutClick,
      provider,
      providerSet,
      renderPreviewPass,
      setTriggerAuthCheck,
      toggleMvpdAuthOverlay,
    };

    const onMobileMenuClick = () => {
      const { setOverlay } = this.props;
      this.callCollectionImpressionEvent();
      setOverlay(OVERLAY_HEADER_DROPDOWN);

      const playerStatus = getPlayerState(PLAYER_REFERENCE_GLOBAL);
      // check if player was playing first
      if (playerStatus !== 'READY') {
        if (!menuOpen) {
          playerPause(PLAYER_REFERENCE_GLOBAL);
        } else {
          playerPlay(PLAYER_REFERENCE_GLOBAL);
        }
      }
    };

    return (
      <div className={DefaultHeaderStyle.headerContentWrapper}>
        <div className={classNames(DefaultHeaderStyle.content, DefaultHeaderStyle.fullWidth)}>
          <div className={DefaultHeaderStyle.leftContent}>
            <div className={DefaultHeaderStyle.logoProviderWrapper}>{this.renderIndexLink()}</div>
          </div>
          <Categories {...categoryProps} />
          {menuOpen && MobileCloseButton(onMobileMenuClick)}
          {!subscriptionPaused && (
            <div className={classNames(rightContentClasses)}>
              {getOptimizelySearchBoxEnabled() && <SearchBox />}
              <ProviderLogo {...providerProps} />
              <div className={DefaultHeaderStyle.iconContainer}>
                <SearchIcon
                  onClick={this.onSearchClick}
                  isActive={getCurrentPath().includes('/find')}
                />
                {profileIcon}
              </div>
              {HamburgerButton(onMobileMenuClick)}
            </div>
          )}
          {subscriptionPaused && (
            <div className={classNames(rightContentClasses)}>
              <UpgradeButton
                isResumeButton
                data-test="upgrade-button"
                onClick={() => redirectToExternalUrl(helperGetAccountUrl())}
              />
              <div className={DefaultHeaderStyle.iconContainer}>{profileIcon}</div>
              {HamburgerButton(onMobileMenuClick)}
            </div>
          )}
        </div>
      </div>
    );
  }

  renderIndexLink = () => {
    const {
      accessToken,
      account,
      breakpoint,
      deviceId,
      fetchCollectionsOnHeaderClick,
      isFreemium,
      preauthorizedResources,
      upcomingOverlayOpen,
    } = this.props;
    const [home] = _.get(window.ExternalConfig, 'navigation', []);
    const { screenUrl, label } = home;
    return (
      <NetworkLink
        breakpoint={breakpoint}
        preauthorizedResources={preauthorizedResources}
        theme={Style}
        alt="Fox Nation logo"
        clickEvent={e => {
          if (screenUrl) {
            fetchCollectionsOnHeaderClick({
              accessToken,
              account,
              deviceId,
              panelsToPrefetch: 3,
              path: screenUrl,
              screenName: label,
            });
          }
          this.onNavClick({ e, text: getTealiumCurrentBrand() });
          handleAnalytics({
            event: Events.GLOBAL_NAV_CLICKED,
            payload: { page_item_title: 'Network Logo' },
          });
          upcomingOverlayOpen(false);
        }}
        isHeader
        isFreemium={isFreemium}
      />
    );
  };

  renderLink = (props, children, idx) => {
    const route = _.get(props, 'to');
    const targetData = getUrlTarget(route);
    const anchorProps = { ...props, ...targetData };
    const key = `link${route}.${idx}`;
    let altChildren;
    if (urlIsExternal(route)) {
      const isExternal = urlIsExternal(route);
      if (isExternal) {
        anchorProps.href = route;
      } else if (!isExternal && route) {
        anchorProps.href = `${BaseUrl}${route}`;
      }
      return (
        <a key={key} {...anchorProps}>
          {altChildren || children}
        </a>
      );
    }

    return (
      <Link
        key={key}
        {...anchorProps}
        aria-label={typeof children === 'string' && children}
        tab-index="0"
      >
        {altChildren || children}
      </Link>
    );
  };

  renderProfileIcon() {
    if (!ProfileEnabled || isStaticRender() || this.disableAuthentication()) return null;

    const { account, setAccountCloseIgnoreRedirect } = this.props;
    const { profileMenu_profileSignIn: profileSignInText } = window.Language;
    let label = (
      <button
        className={DefaultHeaderStyle.profileButton}
        type="button"
        aria-label="sign up or login to profile"
      >
        {profileSignInText}
      </button>
    );
    if (account) {
      const username = _.get(account, 'firstName');
      label = (
        <button className={Style.userButton} type="button" aria-label="manage your profile">
          {username}
        </button>
      );
    }
    const props = {
      to: helperGetAccountUrl(),
      key: 'headerProfileIcon',
      onClick: () => {
        this.callCollectionImpressionEvent();
        setAccountCloseIgnoreRedirect(false);
      },
      'aria-label': 'Profile Icon',
    };
    return <div data-test="profile-icon">{this.renderLink(props, label)}</div>;
  }

  renderMobileProfile() {
    if (!ProfileEnabled || isStaticRender() || this.disableAuthentication()) return null;
    const { account, setAccountCloseIgnoreRedirect } = this.props;
    const { profile_header: profileLabel, auth_signInButton: signInLabel } = window.Language;
    const style = classNames(Style.categoryItemMobilev2, Style.mobileSplit);
    let label = (
      <a className={style} aria-label="sign up or login to profile">
        <span>{profileLabel}</span>
        <span>{signInLabel}</span>
      </a>
    );
    if (account || isMvpdSubscription()) {
      const username = _.get(account, 'firstName');
      const displayName = username ? `${username}'s profile` : 'My Account';
      label = (
        <div className={style} aria-label="manage your profile">
          <span>{displayName}</span>
          <span>
            <Icon className={Style.rightArrow} size="large" color="grey" icon="arrow-right" />
          </span>
        </div>
      );
    }
    const props = {
      to: helperGetAccountUrl(),
      key: 'headerProfileIcon',
      onClick: () => {
        this.callCollectionImpressionEvent();
        setAccountCloseIgnoreRedirect(false);
      },
      'aria-label': 'Profile Icon',
    };
    return <div data-test="profile-icon">{this.renderLink(props, label)}</div>;
  }

  renderProvider = () => {
    if (SingleMvpd || this.disableAuthentication()) return null;
    const { breakpoint, provider } = this.props;
    const { profileLogout_logoutButton: signoutLabel } = window.Language;

    const { hamburgerMenuEnabled } = BREAKPOINTS[breakpoint];
    const { providerSet, renderPreviewPass } = this.state;
    const mvpd = _.get(provider, 'adobePassId');
    const hasProvider = !!provider;
    const isSearchActive = this.isSearchActive();

    if (!hasProvider || isSearchActive || (isPreviewPass(mvpd) && !renderPreviewPass)) {
      return null;
    }

    const secondaryLogo = _.get(provider, 'logos[0].url');
    const providerLogo = forceHttps(_.get(provider, 'logos[1].url', secondaryLogo));
    let providerClasses = Style.providerLogo;

    if (!hamburgerMenuEnabled) {
      providerClasses = classNames(Style.rightIcon, providerClasses, Style.providerLogoDesktop);
    }
    const signOutButton = () => {
      if (isPreviewPass(mvpd) || isMvpdSubscription()) return null;
      return (
        <div className={Style.signOutContainer}>
          <button
            className={Style.signOutButton}
            onClick={e => {
              this.onLogoutClick(e);
            }}
          >
            {signoutLabel}
          </button>
        </div>
      );
    };

    if (!providerLogo || !providerSet) return null;
    let image = <img src={providerLogo} alt={_.get(provider, 'name', 'Cable Provider Logo')} />;
    if (provider.siteUrl) {
      image = this.renderLink({ to: provider.siteUrl, target: '_self' }, image);
    }
    return (
      <div className={providerClasses}>
        {image}
        {signOutButton()}
      </div>
    );
  };

  renderMobileProvider = () => {
    if (SingleMvpd || this.disableAuthentication()) return null;
    const { provider, setTriggerAuthCheck, toggleMvpdAuthOverlay } = this.props;
    const {
      mvpdSignIn_label: mvpdLabel,
      auth_signInButton: signInLabel,
      profileLogout_logoutButton: signoutLabel,
    } = window.Language;
    const { providerSet, renderPreviewPass } = this.state;
    const mvpd = _.get(provider, 'adobePassId');
    const hasProvider = !!provider;
    const style = classNames(Style.categoryItemMobilev2, Style.mobileSplit);
    const isSearchActive = this.isSearchActive();
    const mvpdIsPreviewPass = isPreviewPass(mvpd);

    if (!hasProvider || isSearchActive || (mvpdIsPreviewPass && !renderPreviewPass)) {
      const options = {
        onOverlayClose: () => {
          setTriggerAuthCheck(true);
          toggleMvpdAuthOverlay(false);
        },
      };
      const onClick = () => {
        setTriggerAuthCheck(false);
        toggleMvpdAuthOverlay(true, options, false);
      };
      return (
        <a className={style} onClick={onClick}>
          <span>{mvpdLabel}</span>
          <span>{signInLabel}</span>
        </a>
      );
    }

    const secondaryLogo = _.get(provider, 'logos[0].url');
    const providerLogo = forceHttps(_.get(provider, 'logos[1].url', secondaryLogo));

    if (!providerLogo || !providerSet) return null;
    let image = <img src={providerLogo} alt={_.get(provider, 'name', 'Cable Provider Logo')} />;
    if (provider.siteUrl) {
      image = this.renderLink({ to: provider.siteUrl, target: '_self' }, image);
    }
    const signOutElement =
      mvpdIsPreviewPass || isMvpdSubscription() ? null : (
        <span
          onClick={e => {
            this.onLogoutClick(e);
          }}
        >
          {signoutLabel}
        </span>
      );
    return (
      <a className={style}>
        <span>{image}</span>
        {signOutElement}
      </a>
    );
  };

  renderSmartBanner() {
    const { showAppDownloadPrompt } = this.props;

    if (isStaticRender() || !showAppDownloadPrompt) {
      return null;
    }

    const { isLivePage, isWatchPage, campaignOverrides, setSmartBannerEnabled } = this.props;
    return (
      <SmartBanner
        {...smartBannerSettings({
          campaignOverrides,
          isLive: isLivePage,
          isWatch: isWatchPage,
          onClose: () => setSmartBannerEnabled(false),
          onInstall: () => setSmartBannerEnabled(false),
        })}
      />
    );
  }

  renderSignInButton() {
    const { auth_signInButton: signInText } = window.Language;
    return (
      <div
        data-test="upsell-sign-in-text"
        className={Style.accountStatus}
        onClick={() => {
          handleAnalytics({
            event: Events.GLOBAL_NAV_CLICKED,
            payload: { page_item_title: signInText },
          });
          this.onSignInClick({ text: signInText });
        }}
      >
        {signInText}
      </div>
    );
  }

  closeModal = () => {
    handleAnalytics({
      event: Events.MODAL_CLOSED,
      payload: {
        page_start_source: 'modal checkout',
        page_plan_flow_type: 'new user acquisition',
        page_item_title: 'x',
        page_action_type: 'close modal',
      },
    });
    window.setTimeout(refreshPage, 500);
  };

  renderUpsellContent() {
    const {
      account,
      breakpoint,
      favoritesManager,
      geoLocationBlocked: isBlocked,
      isFreemium,
      isAnonymous,
      location,
      maintenance: isMaintenance,
      params,
      campaignOverrides,
      isLivePage,
      isWatchPage,
      subscriptionPaused,
    } = this.props;
    const menuOpen = this.isMenuOpen();
    const { renderProfileDropdown } = this.state;
    const brandId = window.ExternalConfig.brand.id;
    const isArticle = toBoolean(_.get(params, 'articleId', false));
    const displayLogin = toBoolean(_.get(location, 'query.displayLogin', true));
    const hideAccountLinks = isArticle && !displayLogin;
    const breakpointIsMobile = this.isMobile();
    const providerLogo = this.renderProvider();
    const { foxAwesomeLayout } = BREAKPOINTS[breakpoint];

    const contentClasses = foxAwesomeLayout ? [Style.foxAwesomeContent] : [Style.content];
    contentClasses.push(Style.fullWidth);
    let mobileProviderLogo;

    if (breakpointIsMobile) {
      mobileProviderLogo = providerLogo;
    }

    const isSimplifiedHeader = isBlocked || isMaintenance;

    if (isSimplifiedHeader) {
      contentClasses.push(Style.maintenance);
      return <div className={classNames(contentClasses)}>{this.renderIndexLink()}</div>;
    }

    const upgradeButton = breakpoint !== SITE_PLATFORM_MOBILE && (
      <UpgradeButton
        isResumeButton={subscriptionPaused}
        isFreemium={isFreemium}
        data-test="upgrade-button"
        onClick={props => {
          if (subscriptionPaused) {
            redirectToExternalUrl(helperGetAccountUrl());
          } else {
            this.onSignUpClick(props);
          }
        }}
      />
    );

    const buythroughProfileIconProps = {
      account,
      favoritesManager,
      isAnonymous,
      isFreemium,
      isWeb: this.isWeb,
      renderProfileDropdown,
      setWrapperRef: this.setWrapperRef,
      toggleProfileDropdown: this.toggleProfileDropdown,
    };

    const profileButton =
      !isAnonymous || isMvpdSubscription()
        ? BuythroughProfileIcon(buythroughProfileIconProps)
        : this.renderSignInButton();

    const accountLinks =
      hideAccountLinks || isStaticRender() ? null : (
        <div className={Style.accountLinks}>
          {upgradeButton}
          <div>
            <SearchIcon
              onClick={this.onSearchClick}
              isActive={getCurrentPath().includes('/find')}
            />
          </div>
          {profileButton}
        </div>
      );

    const categoryProps = {
      campaignOverrides,
      desktopCategories: true,
      getGlobalNavItems: this.getGlobalNavItems,
      isAnonymous: true,
      isLivePage,
      isMobileActive: this.isMobileActive,
      isWatchPage,
      menuOpen,
    };

    return (
      <div className={Style.headerContentWrapper} data-test="upsell-header">
        <div className={classNames(contentClasses)}>
          <div className={Style.innerContent}>
            <div className={classNames(Style.logoProviderWrapper, Style[`brand-${brandId}`])}>
              {this.renderIndexLink()}
              {mobileProviderLogo}
            </div>
            <Categories {...categoryProps} />
          </div>
          {accountLinks}
        </div>
      </div>
    );
  }

  render() {
    const {
      account,
      appView,
      campaignOverrides,
      getCurrentPage,
      isAppViewExcluded,
      isFreemium,
      isHeader,
      isHeaderPinned,
      isHomePage,
      isSmartBannerEnabled,
      isVideoPage,
      isListPage,
      isLivePage,
      isWatchPage,
      overlay,
      isPlayerLoaded,
      maintenance: isMaintenance,
      breakpoint,
      preauthorizedResources,
      useSimplified,
    } = this.props;
    const { hoverMenuDisplayed } = this.state;
    const {
      darkNavOnHomepage,
      darkNavOnListing,
      darkNavOnNetworkUpsellPage,
      darkNavOnVideo,
      hamburgerMenuEnabled,
      smartBannerBar,
    } = BREAKPOINTS[breakpoint];

    if (appView && isAppViewExcluded(NAME)) return null;

    const isNetworkUpsellPage = getCurrentPage() === PAGE_NETWORK_UPSELL;

    const browser = determineBrowser();
    const breakpointIsMobile = this.isMobile();
    const mobileActive = this.isMobileActive();
    const externalHide = isHeader === false && isHeaderPinned;

    const softHide =
      isVideoPage && externalHide && !overlay && isPlayerLoaded && !breakpointIsMobile
        ? Style.softHide
        : null;
    const darkNav =
      (isNetworkUpsellPage && darkNavOnNetworkUpsellPage) ||
      (isHomePage && (darkNavOnHomepage || hamburgerMenuEnabled)) ||
      (isListPage && darkNavOnListing) ||
      (isVideoPage && darkNavOnVideo);
    const hoverMenuOpen = hoverMenuDisplayed;

    const mobileContainerStyle = Style.containerMobilev2Nation;

    const container = classNames([
      browser,
      Style.Header,
      softHide,
      mobileActive ? mobileContainerStyle : null,
      darkNav ? Style.containerListPage : null,
      hoverMenuOpen ? Style.containerHoverMenu : null,
      getFontClassName(Style),
      Style.redesignCollections,
      this.isMenuOpen() ? Style.clearHeader : null,
    ]);

    const menuOpen = this.isMenuOpen();
    const searchActive = this.isSearchActive();
    const categoryProps = {
      campaignOverrides,
      desktopCategories: false,
      getGlobalNavItems: this.getGlobalNavItems,
      isLivePage,
      isMobileActive: this.isMobileActive,
      isWatchPage,
      menuOpen,
    };
    let mobileCategories = null;
    if (mobileActive && !isMaintenance) {
      categoryProps.mobileCategories = true;
      mobileCategories = Categories(categoryProps);
    }
    const hideClass = menuOpen || searchActive ? Style.hideDownloadBanner : '';
    const mobileBannerClass = isSmartBannerEnabled ? Style.downloadBanner : '';
    const smartBannerTags = smartBannerBar && (
      <div data-test="header-mobile-banner" className={classNames(mobileBannerClass, hideClass)}>
        {this.renderSmartBanner()}
      </div>
    );

    const displayUpsell = canDisplayUpsell(preauthorizedResources);
    const isAuthFreemiumMobile = breakpointIsMobile && isFreemium && account;
    const headerContent =
      displayUpsell && !isAuthFreemiumMobile ? this.renderUpsellContent() : this.renderContent();
    const headerItemsBannerClass = isSmartBannerEnabled ? Style.headerItems : '';

    return (
      <>
        <header data-test="header" className={container} tab-index="0">
          <Headroom
            disableInlineStyles
            disable={this.isMobileActive()}
            onUnpin={this.onUnpin}
            onUnfix={this.onUnfix}
          >
            {!breakpointIsMobile && <NetworkBar breakpoint={breakpoint} />}
            <div className={headerItemsBannerClass}>
              {smartBannerTags}
              {headerContent}
            </div>
          </Headroom>
          {mobileCategories}
        </header>
        {useSimplified && <SignUpModalContainer closeModal={this.closeModal} />}
      </>
    );
  }
}

Header.defaultProps = {
  accessToken: null,
  account: null,
  buythroughScenario: null,
  campaignOverrides: {},
  clearTimeout: noop,
  collectionImpressionCalled: noop,
  coordinates: null,
  deviceId: null,
  dynamicNavigation: null,
  favoritesManager: {},
  fetchCollectionsOnHeaderClick: noop,
  fetchMVPDProviders: noop,
  geoLocationBlocked: null,
  getCurrentPage: noop,
  isAnonymous: true,
  isAuthenticated: false,
  isFreemium: false,
  isHeader: null,
  isHeaderPinned: null,
  isListPage: false,
  isLivePage: false,
  isNavScheduleRendered: false,
  isPlayerLoaded: false,
  isSmartBannerEnabled: null,
  isVideoPage: null,
  isWatchPage: false,
  isSeriesDetailPage: false,
  logout: noop,
  mvpd: null,
  mvpdData: null,
  mvpdLogout: noop,
  mvpdProviders: null,
  onNavClick: noop,
  onSimplifiedSignUpClick: noop,
  onSearchClick: noop,
  overlay: null,
  params: {},
  preauthorizedResources: null,
  provider: null,
  setAccountCloseIgnoreRedirect: noop,
  setAuthorizationToken: noop,
  setCoordinates: noop,
  setMVPDSignInTrigger: noop,
  setMVPDToAppState: noop,
  setNavScheduleRendered: noop,
  setSmartBannerEnabled: noop,
  setTimeout: noop,
  setTriggerAuthCheck: noop,
  showAppDownloadPrompt: false,
  toggleMvpdAuthOverlay: noop,
  upcomingOverlayOpen: noop,
  useSimplified: false,
  visibleCollections: {},
};

Header.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  appView: PropTypes.bool.isRequired,
  breakpoint: PropTypes.string.isRequired,
  buythroughScenario: PropTypes.string,
  campaignOverrides: PropTypes.shape({}),
  clearTimeout: PropTypes.func,
  collectionImpressionCalled: PropTypes.func,
  coordinates: PropTypes.shape({}),
  deviceId: PropTypes.string,
  dynamicNavigation: PropTypes.arrayOf(PropTypes.shape({})),
  favoritesManager: PropTypes.shape({}),
  fetchCollectionsOnHeaderClick: PropTypes.func,
  fetchMVPDProviders: PropTypes.func,
  geoLocationBlocked: PropTypes.bool,
  getCurrentPage: PropTypes.func,
  isAppViewExcluded: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  isAnonymous: PropTypes.bool,
  isFreemium: PropTypes.bool,
  isHeader: PropTypes.bool,
  isHeaderPinned: PropTypes.bool,
  isHomePage: PropTypes.bool.isRequired,
  isListPage: PropTypes.bool,
  isLivePage: PropTypes.bool,
  isNavScheduleRendered: PropTypes.bool,
  isPlayerLoaded: PropTypes.bool,
  isSeriesDetailPage: PropTypes.bool,
  isSmartBannerEnabled: PropTypes.bool,
  isVideoPage: PropTypes.bool,
  isWatchPage: PropTypes.bool,
  location: PropTypes.shape({}).isRequired,
  logout: PropTypes.func,
  maintenance: PropTypes.bool.isRequired,
  mvpd: PropTypes.shape({}),
  mvpdData: PropTypes.shape({}),
  mvpdLogout: PropTypes.func,
  mvpdProviders: PropTypes.arrayOf(PropTypes.shape({})),
  onNavClick: PropTypes.func,
  onSimplifiedSignUpClick: PropTypes.func,
  onSearchClick: PropTypes.func,
  overlay: PropTypes.string,
  params: PropTypes.shape({}),
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  preauthRefreshComplete: PropTypes.bool.isRequired,
  profile: PropTypes.shape({}),
  provider: PropTypes.shape({
    logos: PropTypes.array,
  }),
  setAccountCloseIgnoreRedirect: PropTypes.func,
  setAuthorizationToken: PropTypes.func,
  setCoordinates: PropTypes.func,
  setHeaderPin: PropTypes.func.isRequired,
  setIsAuthenticated: PropTypes.func.isRequired,
  setMVPDSignInTrigger: PropTypes.func,
  setMVPDToAppState: PropTypes.func,
  setNavScheduleRendered: PropTypes.func,
  setOverlay: PropTypes.func.isRequired,
  setPreauthorizedResources: PropTypes.func.isRequired,
  setPreauthRefreshComplete: PropTypes.func.isRequired,
  setProvider: PropTypes.func.isRequired,
  setSmartBannerEnabled: PropTypes.func,
  setTimeout: PropTypes.func,
  setTriggerAuthCheck: PropTypes.func,
  showAppDownloadPrompt: PropTypes.bool,
  subscriptionPaused: PropTypes.bool,
  toggleMvpdAuthOverlay: PropTypes.func,
  upcomingOverlayOpen: PropTypes.func,
  useSimplified: PropTypes.bool,
  visibleCollections: PropTypes.shape({}),
};

function mapStateToProps(state) {
  const visibleCollections = _.get(state, 'collection.visibleCollections', null);
  return { visibleCollections };
}

export default React.memo(
  ReactTimeout(
    connect(mapStateToProps, {
      setPreauthorizedResources: setPreauthorizedResourcesAction,
      fetchMVPDProviders: fetchMVPDProvidersAction,
      fetchCollectionsOnHeaderClick,
    })(withAuth(Header)),
  ),
);
